import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../ModuleNav';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Col,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Segmented,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AlertOutlined,
  BellOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  MailOutlined,
  MobileOutlined,
  NotificationOutlined,
  RedoOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import lib from '../../assets/utils/lib';
import {
  CAMPAIGN_AUDIENCE_LABELS,
  CAMPAIGN_STATUS_COLORS,
  CAMPAIGN_TYPE_META,
  CAMPAIGN_TYPES,
  pushTypeForCampaign,
  stripHtml,
  validateCampaignForm,
} from '../../types/campaign';
import {
  CampaignsHowToButton,
  CampaignsHowToModal,
  useCampaignsHowTo,
} from './CampaignsHowToGuide';
import EmailWysiwygEditor from './EmailWysiwygEditor';
import './CampaignsPage.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/** App convention: campaign timestamps are Africa/Lagos wall-clock. */
const APP_TZ = 'Africa/Lagos';

const TYPE_COLORS = {
  system: '#0891b2',
  marketing: '#0f766e',
  maintenance: '#ea580c',
  emergency: '#dc2626',
};

const TYPE_ICONS = {
  system: <DownloadOutlined />,
  marketing: <BellOutlined />,
  maintenance: <ToolOutlined />,
  emergency: <AlertOutlined />,
};

const TYPE_SEGMENTED = CAMPAIGN_TYPES.map((value) => ({
  value,
  label: CAMPAIGN_TYPE_META[value]?.label || value,
  icon: TYPE_ICONS[value],
}));

/**
 * Format API datetimes. Naive MySQL strings are treated as Africa/Lagos
 * (not browser-local / not UTC), matching api_v1 storage convention.
 */
const fmtDateTime = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
  const d = hasOffset
    ? dayjs(normalized).tz(APP_TZ)
    : dayjs.tz(normalized, APP_TZ);
  if (!d.isValid()) return raw;
  return d.format('MMM D, YYYY HH:mm');
};

const RECIPIENT_STATUS_COLORS = {
  pending: 'gold',
  sent: 'green',
  failed: 'red',
  skipped: 'default',
};

/** Derive a single display status from a push ticket's (ticket_status, receipt_status) pair. */
const deviceStatus = (row) => {
  if (row.ticket_status === 'error') return { label: 'failed', color: 'red' };
  if (row.receipt_status === 'ok') return { label: 'delivered', color: 'green' };
  if (row.receipt_status === 'error') return { label: 'failed', color: 'red' };
  if (row.receipt_status === 'pending') return { label: 'pending', color: 'gold' };
  return { label: 'accepted', color: 'blue' };
};

const formatEmailReach = (r) => {
  const sent = Number(r.sent_count || 0);
  const failed = Number(r.failed_count || 0);
  const estimated = Number(r.estimated_recipients || 0);
  if (['sent', 'failed'].includes(r.status) || sent > 0 || failed > 0) {
    if (failed > 0) return `${sent.toLocaleString()} sent · ${failed.toLocaleString()} failed`;
    return `${sent.toLocaleString()} sent`;
  }
  if (estimated > 0) return `~${estimated.toLocaleString()} est.`;
  return '—';
};

const formatPushReach = (r) => {
  const count = Number(r.recipient_count ?? r.estimated_recipients ?? 0);
  if (['sent', 'failed'].includes(r.status)) {
    return `${count.toLocaleString()} devices`;
  }
  if (count > 0) return `~${count.toLocaleString()} devices`;
  return '—';
};

const normalizeAudiencePayload = (raw) => {
  if (!raw) return { audiences: [], filterOptions: {} };
  if (Array.isArray(raw.audiences)) {
    return { audiences: raw.audiences, filterOptions: raw.filter_options || {} };
  }
  if (Array.isArray(raw)) {
    return { audiences: raw, filterOptions: {} };
  }
  return { audiences: [], filterOptions: {} };
};

const FilterControl = ({ def, value, onChange, optionsMap }) => {
  const options = useMemo(() => {
    if (Array.isArray(def.options) && def.options.length) {
      return def.options.map((o) => ({ value: o.value, label: o.label }));
    }
    if (def.options_source && optionsMap[def.options_source]) {
      return optionsMap[def.options_source];
    }
    return [];
  }, [def, optionsMap]);

  if (def.type === 'boolean') {
    return <Switch checked={Boolean(value)} onChange={(checked) => onChange(checked || undefined)} />;
  }
  if (def.type === 'number') {
    return (
      <InputNumber
        style={{ width: '100%' }}
        value={value}
        min={def.min}
        max={def.max}
        onChange={(v) => onChange(v === null ? undefined : v)}
        placeholder={def.placeholder || def.label}
      />
    );
  }
  if (def.type === 'select') {
    return (
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        mode={def.multiple ? 'multiple' : undefined}
        style={{ width: '100%' }}
        value={value}
        options={options}
        onChange={(v) => onChange(v === undefined || v === null || (Array.isArray(v) && !v.length) ? undefined : v)}
        placeholder={def.placeholder || `Select ${def.label}`}
      />
    );
  }
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      placeholder={def.placeholder || def.label}
    />
  );
};

const ChannelCard = ({ channel, active, onToggle, title, hint, icon }) => (
  <button
    type="button"
    className={`campaigns-channel-card channel-${channel}${active ? ' is-active' : ''}`}
    onClick={onToggle}
    aria-pressed={active}
  >
    <span className="campaigns-channel-card__check" aria-hidden />
    <div className="campaigns-channel-card__top">
      <span className="campaigns-channel-card__icon">{icon}</span>
      <span className="campaigns-channel-card__name">{title}</span>
    </div>
    <p className="campaigns-channel-card__hint">{hint}</p>
  </button>
);

const PushPreview = ({ type, title, body }) => {
  const color = TYPE_COLORS[type] || TYPE_COLORS.system;
  const plain = stripHtml(body);
  return (
    <div className="push-preview">
      <div className="push-preview__row">
        <div className="push-preview__badge" style={{ background: color }}>
          {TYPE_ICONS[type] || <MobileOutlined />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="push-preview__app">
            <MobileOutlined /> Aman HMO · now
          </div>
          <div className="push-preview__title">
            {(title || 'Notification title').slice(0, 60)}
          </div>
          <Paragraph className="push-preview__body" ellipsis={{ rows: 3 }}>
            {(plain || 'Notification message preview').slice(0, 250)}
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

/**
 * Full-fidelity preview: fetches the exact branded HTML BrandEmailLayout::wrap()
 * would produce for this subject/body/type (same code path as the real send) and
 * renders it in an iframe — not a hand-approximated mock. Debounced so typing
 * doesn't fire a request per keystroke.
 */
const EmailPreview = ({ subject, body, type }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!subject?.trim() && !body?.trim()) {
      setHtml('');
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await lib.previewEmailCampaignHtml({
          subject: subject || '',
          body_html: body || '',
          type,
        });
        const parsed = lib.parseApiEnvelope(resp);
        if (parsed.ok) setHtml(parsed.data?.html || '');
      } catch (e) {
        // Keep the last good preview on transient failure — no need to surface this.
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [subject, body, type]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html || '<body style="margin:0;padding:24px;font-family:sans-serif;color:#94a3b8;">Start typing to see the full email…</body>');
    doc.close();
  }, [html]);

  return (
    <div className="email-preview-frame-wrap">
      {loading && <div className="email-preview-frame__loading">Updating preview…</div>}
      <iframe ref={iframeRef} title="Full email preview" className="email-preview-frame" />
    </div>
  );
};

/**
 * Unified campaign composer: Push and/or Email in one flow.
 */
/** Channels preselected by the sidebar entry: Push notifications, Email notifications, or both. */
const channelsFor = (channelParam) => {
  if (channelParam === 'push') return { push: true, email: false };
  if (channelParam === 'email') return { push: false, email: true };
  return { push: true, email: true };
};

const CampaignsPage = () => {
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get('channel');
  const {
    open: howToOpen,
    required: howToRequired,
    openGuide,
    acknowledge: acknowledgeHowTo,
    closeOptional: closeHowTo,
  } = useCampaignsHowTo();

  const [activeTab, setActiveTab] = useState('compose');
  const [channels, setChannels] = useState(() => channelsFor(channelParam));
  // Push audience and landing page — the same model announcements use.
  const [pushOptions, setPushOptions] = useState({ landing_pages: [], organizations: [], plans: [], access_types: [] });
  const [pushTargets, setPushTargets] = useState({ org_ids: [], plan_ids: [], access_types: [] });
  const [pushLanding, setPushLanding] = useState(null);
  const [type, setType] = useState('system');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState(null);
  const [saveAsDraft, setSaveAsDraft] = useState(false);

  const [selectedAudiences, setSelectedAudiences] = useState(['clients']);
  const [audienceMeta, setAudienceMeta] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    lib.getBroadcastOptions().then((resp) => {
      const parsed = lib.parseApiEnvelope(resp);
      if (alive && parsed.ok && parsed.data) setPushOptions(parsed.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const [historyChannel, setHistoryChannel] = useState(channelParam === 'push' || channelParam === 'email' ? channelParam : 'all');

  // Moving between the sidebar's Push and Email entries re-targets the composer and the
  // history filter without a reload.
  useEffect(() => {
    setChannels(channelsFor(channelParam));
    setPushTargets({ org_ids: [], plan_ids: [], access_types: [] });
    setPushLanding(null);
    setHistoryChannel(channelParam === 'push' || channelParam === 'email' ? channelParam : 'all');
  }, [channelParam]);
  const [cancellingKey, setCancellingKey] = useState(null);
  const [retryingKey, setRetryingKey] = useState(null);
  const [retryingItemId, setRetryingItemId] = useState(null);

  const [auditRow, setAuditRow] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [auditCounts, setAuditCounts] = useState({});
  const [auditFilter, setAuditFilter] = useState('all');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPagination, setAuditPagination] = useState({ page: 1, pageSize: 50, total: 0 });

  const usePush = Boolean(channels.push);
  const useEmail = Boolean(channels.email);
  const marketingPushConflict = usePush && type === 'marketing';

  const fetchAudiences = useCallback(async () => {
    try {
      const resp = await lib.getEmailCampaignAudiences();
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) return;
      const { audiences, filterOptions: opts } = normalizeAudiencePayload(parsed.data);
      setAudienceMeta(audiences);
      setFilterOptions(opts);
    } catch (e) {
      // audiences only needed for email
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [emailResp, pushResp] = await Promise.all([
        lib.getEmailCampaignHistory(1, 50).catch(() => null),
        lib.getBroadcastHistory(1, 50).catch(() => null),
      ]);

      const emailParsed = emailResp ? lib.parseApiEnvelope(emailResp) : null;
      const pushParsed = pushResp ? lib.parseApiEnvelope(pushResp) : null;

      const emailItems = emailParsed?.ok
        ? (Array.isArray(emailParsed.data?.items) ? emailParsed.data.items : (Array.isArray(emailParsed.data) ? emailParsed.data : []))
        : [];
      const pushItems = pushParsed?.ok
        ? (Array.isArray(pushParsed.data) ? pushParsed.data : (Array.isArray(pushParsed.data?.items) ? pushParsed.data.items : []))
        : [];

      const merged = [
        ...emailItems.map((r) => ({
          key: `email-${r.id}`,
          channel: 'email',
          id: r.id,
          type: r.type,
          title: r.subject,
          status: r.status,
          when: r.sent_at || r.scheduled_at || r.created_at,
          detail: Array.isArray(r.audiences) ? r.audiences.join(', ') : '',
          counts: formatEmailReach(r),
          error_message: r.error_message,
          raw: r,
        })),
        ...pushItems.map((r) => ({
          key: `push-${r.id}`,
          channel: 'push',
          id: r.id,
          type: r.type,
          title: r.title,
          status: r.status,
          when: r.sent_at || r.scheduled_at || r.created_at,
          detail: r.status_label || 'Client app devices',
          counts: formatPushReach(r),
          error_message: r.error_message,
          raw: r,
        })),
      ].sort((a, b) => {
        const ta = dayjs.tz(String(a.when || '').replace(' ', 'T'), APP_TZ).valueOf() || 0;
        const tb = dayjs.tz(String(b.when || '').replace(' ', 'T'), APP_TZ).valueOf() || 0;
        return tb - ta;
      });

      setRows(merged);
    } catch (e) {
      message.error(e?.message || 'Failed to load campaign history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudiences();
  }, [fetchAudiences]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const audienceOptions = useMemo(() => {
    if (audienceMeta.length) {
      return audienceMeta.map((a) => ({
        value: a.key,
        label: a.label || CAMPAIGN_AUDIENCE_LABELS[a.key] || a.key,
      }));
    }
    return Object.entries(CAMPAIGN_AUDIENCE_LABELS).map(([value, label]) => ({ value, label }));
  }, [audienceMeta]);

  const setFilterValue = (audience, key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [audience]: { ...(prev[audience] || {}) } };
      if (value === undefined || value === null || value === '') {
        delete next[audience][key];
        if (Object.keys(next[audience]).length === 0) delete next[audience];
      } else {
        next[audience][key] = value;
      }
      return next;
    });
    setPreview(null);
  };

  const activeFilterCount = useMemo(() => (
    selectedAudiences.reduce((sum, key) => sum + Object.keys(filters[key] || {}).length, 0)
  ), [selectedAudiences, filters]);

  const resetForm = () => {
    setChannels(channelsFor(channelParam));
    setType('system');
    setTitle('');
    setBody('');
    setScheduledAt(null);
    setSaveAsDraft(false);
    setSelectedAudiences(['clients']);
    setFilters({});
    setPreview(null);
  };

  const formSnapshot = () => ({
    channels,
    type,
    title,
    subject: title,
    body,
    body_html: body,
    audiences: selectedAudiences,
  });

  const handlePreview = async () => {
    if (!useEmail) {
      message.info('Push reaches all active client app devices. Enable Email to preview segmented recipients.');
      return;
    }
    if (!selectedAudiences.length) {
      message.error('Select at least one audience');
      return;
    }
    setPreviewLoading(true);
    try {
      const resp = await lib.previewEmailCampaign({
        audiences: selectedAudiences,
        filters,
        sample_limit: 15,
      });
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        message.error(parsed.message || 'Preview failed');
        return;
      }
      setPreview(parsed.data || {});
    } catch (e) {
      message.error(e?.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendClick = () => {
    const err = validateCampaignForm(formSnapshot());
    if (err) {
      message.error(err);
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSending(true);
    const scheduled_at = scheduledAt
      ? scheduledAt.tz(APP_TZ).format('YYYY-MM-DDTHH:mm:ssZ')
      : null;
    const results = [];
    try {
      if (useEmail) {
        const resp = await lib.createEmailCampaign({
          type,
          audiences: selectedAudiences,
          filters,
          subject: title.trim(),
          body_html: body.trim().includes('<') ? body.trim() : `<p>${body.trim().replace(/\n/g, '<br/>')}</p>`,
          body_text: stripHtml(body),
          scheduled_at,
          draft: saveAsDraft,
        });
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          message.error(parsed.message || 'Email campaign failed');
          return;
        }
        results.push(`Email: ${parsed.message || 'queued'} (ID ${parsed.data?.campaign_id})`);
      }

      if (usePush && !saveAsDraft) {
        const pushType = pushTypeForCampaign(type);
        if (!pushType) {
          message.warning('Push skipped for this campaign type');
        } else {
          const pushTitle = title.trim().slice(0, 60);
          const pushBody = stripHtml(body).slice(0, 250);
          const hasPushTargets = Object.values(pushTargets).some((v) => Array.isArray(v) && v.length);
          const resp = await lib.sendBroadcast({
            type: pushType,
            title: pushTitle,
            body: pushBody,
            scheduled_at,
            ...(hasPushTargets ? { targets: pushTargets } : {}),
            ...(pushLanding ? { landing_href: pushLanding } : {}),
          });
          const parsed = lib.parseApiEnvelope(resp);
          if (!parsed.ok) {
            message.error(parsed.message || 'Push broadcast failed');
            if (results.length) message.info(results.join(' · '));
            return;
          }
          const est = parsed.data?.estimated_recipients;
          results.push(
            parsed.data?.status === 'scheduled'
              ? `Push: scheduled (ID ${parsed.data?.broadcast_id})`
              : `Push: sending to ~${Number(est || 0).toLocaleString()} devices`,
          );
        }
      }

      message.success(results.join(' · ') || 'Campaign submitted');
      setConfirmOpen(false);
      resetForm();
      fetchHistory();
      setActiveTab('history');
    } catch (e) {
      message.error(e?.message || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = useCallback(async (row) => {
    setCancellingKey(row.key);
    try {
      if (row.channel === 'email') {
        const resp = await lib.cancelEmailCampaign(row.id);
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          message.error(parsed.message || 'Failed to cancel');
          return;
        }
      } else {
        const resp = await lib.cancelScheduledBroadcast(row.id);
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          message.error(parsed.message || 'Failed to cancel');
          return;
        }
      }
      message.success('Campaign cancelled');
      fetchHistory();
    } catch (e) {
      message.error(e?.message || 'Failed to cancel');
    } finally {
      setCancellingKey(null);
    }
  }, [fetchHistory]);

  const fetchAuditItems = useCallback(async (row, opts = {}) => {
    if (!row) return;
    setAuditLoading(true);
    try {
      if (row.channel === 'email') {
        const resp = await lib.getEmailCampaignRecipients(row.id, {
          status: opts.status,
          page: opts.page ?? 1,
          pageSize: opts.pageSize ?? 50,
        });
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          message.error(parsed.message || 'Failed to load recipients');
          return;
        }
        const payload = parsed.data || {};
        setAuditItems(Array.isArray(payload.items) ? payload.items : []);
        setAuditCounts({
          pending: payload.counts?.pending || 0,
          sent: payload.counts?.sent || 0,
          failed: payload.counts?.failed || 0,
          skipped: payload.counts?.skipped || 0,
        });
        setAuditPagination((p) => ({
          ...p,
          total: payload.total ?? 0,
          page: payload.page ?? p.page,
          pageSize: payload.page_size ?? p.pageSize,
        }));
      } else {
        const resp = await lib.getBroadcastTickets(row.id, {
          filter: opts.status,
          page: opts.page ?? 1,
          pageSize: opts.pageSize ?? 50,
        });
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          message.error(parsed.message || 'Failed to load devices');
          return;
        }
        const payload = parsed.data || {};
        setAuditItems(Array.isArray(payload.items) ? payload.items : []);
        setAuditCounts({
          accepted: payload.counts?.accepted || 0,
          ticket_errors: payload.counts?.ticket_errors || 0,
          delivered: payload.counts?.delivered || 0,
          receipt_errors: payload.counts?.receipt_errors || 0,
          pending_receipts: payload.counts?.pending_receipts || 0,
        });
        setAuditPagination((p) => ({
          ...p,
          total: payload.total ?? 0,
          page: payload.page ?? p.page,
          pageSize: payload.page_size ?? p.pageSize,
        }));
      }
    } catch (e) {
      message.error(e?.message || 'Failed to load audit');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const openAudit = useCallback((row) => {
    setAuditRow(row);
    setAuditFilter('all');
    setAuditPagination({ page: 1, pageSize: 50, total: 0 });
    fetchAuditItems(row, { status: undefined, page: 1, pageSize: 50 });
  }, [fetchAuditItems]);

  const closeAudit = useCallback(() => {
    setAuditRow(null);
    setAuditItems([]);
  }, []);

  const changeAuditFilter = useCallback((filter) => {
    setAuditFilter(filter);
    setAuditPagination((p) => ({ ...p, page: 1 }));
    if (auditRow) {
      fetchAuditItems(auditRow, {
        status: filter === 'all' ? undefined : filter,
        page: 1,
        pageSize: auditPagination.pageSize,
      });
    }
  }, [auditRow, auditPagination.pageSize, fetchAuditItems]);

  const changeAuditPage = useCallback((page, pageSize) => {
    setAuditPagination((p) => ({ ...p, page, pageSize }));
    if (auditRow) {
      fetchAuditItems(auditRow, {
        status: auditFilter === 'all' ? undefined : auditFilter,
        page,
        pageSize,
      });
    }
  }, [auditRow, auditFilter, fetchAuditItems]);

  const handleRetryFailed = useCallback(async (row) => {
    setRetryingKey(row.key);
    try {
      const resp = row.channel === 'email'
        ? await lib.retryFailedEmailCampaign(row.id)
        : await lib.retryBroadcastFailed(row.id);
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        message.error(parsed.message || 'Failed to retry');
        return;
      }
      message.success(parsed.message || 'Retrying failed items');
      fetchHistory();
      if (auditRow?.key === row.key) {
        fetchAuditItems(row, {
          status: auditFilter === 'all' ? undefined : auditFilter,
          page: auditPagination.page,
          pageSize: auditPagination.pageSize,
        });
      }
    } catch (e) {
      message.error(e?.message || 'Failed to retry');
    } finally {
      setRetryingKey(null);
    }
  }, [fetchHistory, auditRow, auditFilter, auditPagination.page, auditPagination.pageSize, fetchAuditItems]);

  const handleRetryOneRecipient = useCallback(async (recipientId) => {
    if (!auditRow || auditRow.channel !== 'email') return;
    setRetryingItemId(recipientId);
    try {
      const resp = await lib.retryFailedEmailCampaign(auditRow.id, [recipientId]);
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        message.error(parsed.message || 'Failed to retry');
        return;
      }
      message.success(parsed.message || 'Retrying');
      fetchAuditItems(auditRow, {
        status: auditFilter === 'all' ? undefined : auditFilter,
        page: auditPagination.page,
        pageSize: auditPagination.pageSize,
      });
    } catch (e) {
      message.error(e?.message || 'Failed to retry');
    } finally {
      setRetryingItemId(null);
    }
  }, [auditRow, auditFilter, auditPagination.page, auditPagination.pageSize, fetchAuditItems]);

  const auditColumns = useMemo(() => {
    if (!auditRow) return [];
    if (auditRow.channel === 'email') {
      return [
        { title: 'Email', dataIndex: 'email', ellipsis: true },
        { title: 'Name', dataIndex: 'name', ellipsis: true, render: (v) => v || '—' },
        {
          title: 'Status',
          dataIndex: 'status',
          width: 100,
          render: (status) => <Tag color={RECIPIENT_STATUS_COLORS[status] || 'default'}>{status}</Tag>,
        },
        {
          title: 'Why',
          key: 'why',
          ellipsis: true,
          render: (_, r) => {
            const text = r.status === 'skipped' ? r.skip_reason : r.error;
            if (!text) return <Text type="secondary">—</Text>;
            return (
              <Tooltip title={text}>
                <Text type={r.status === 'failed' ? 'danger' : 'secondary'}>{text}</Text>
              </Tooltip>
            );
          },
        },
        {
          title: 'Retries',
          key: 'retries',
          width: 110,
          render: (_, r) => {
            const attempts = Number(r.attempts || 0);
            if (r.status === 'pending' && r.next_attempt_at) {
              return (
                <Tooltip title={`Retrying automatically — next attempt after ${fmtDateTime(r.next_attempt_at)}`}>
                  <Tag color="processing">retry {attempts}/3</Tag>
                </Tooltip>
              );
            }
            return attempts > 0 ? <Text type="secondary">{attempts}</Text> : <Text type="secondary">—</Text>;
          },
        },
        { title: 'When', key: 'when', width: 160, render: (_, r) => fmtDateTime(r.sent_at || r.created_at) },
        {
          title: '',
          key: 'retry',
          width: 80,
          render: (_, r) => (r.status === 'failed' ? (
            <Button
              type="link"
              size="small"
              loading={retryingItemId === r.id}
              onClick={() => handleRetryOneRecipient(r.id)}
            >
              Retry
            </Button>
          ) : null),
        },
      ];
    }
    return [
      { title: 'Expo token', dataIndex: 'expo_token', ellipsis: true },
      {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_, r) => {
          const s = deviceStatus(r);
          return <Tag color={s.color}>{s.label}</Tag>;
        },
      },
      {
        title: 'Why',
        key: 'why',
        ellipsis: true,
        render: (_, r) => {
          if (!r.error_message && !r.error_code) return <Text type="secondary">—</Text>;
          const text = r.error_code ? `${r.error_code}: ${r.error_message || ''}` : r.error_message;
          return (
            <Tooltip title={text}>
              <Text type="danger">{text}</Text>
            </Tooltip>
          );
        },
      },
      { title: 'When', key: 'when', width: 160, render: (_, r) => fmtDateTime(r.receipt_checked_at || r.created_at) },
    ];
  }, [auditRow, retryingItemId, handleRetryOneRecipient]);

  const filterPanels = useMemo(() => {
    return selectedAudiences.map((key) => {
      const meta = audienceMeta.find((a) => a.key === key);
      const defs = (Array.isArray(meta?.filters) ? meta.filters : []).map((d) => (
        typeof d === 'string' ? { key: d, label: d, type: 'text' } : d
      ));
      const audienceFilters = filters[key] || {};
      const count = Object.keys(audienceFilters).length;
      return {
        key,
        label: (
          <Space>
            <Text strong>{meta?.label || CAMPAIGN_AUDIENCE_LABELS[key] || key}</Text>
            {count > 0 ? <Tag color="cyan">{count}</Tag> : null}
          </Space>
        ),
        children: (
          <div>
            {meta?.description ? (
              <Paragraph type="secondary" style={{ marginBottom: 12 }}>{meta.description}</Paragraph>
            ) : null}
            {defs.length === 0 ? (
              <Text type="secondary">No granular filters for this audience.</Text>
            ) : (
              <Row gutter={[16, 16]}>
                {defs.map((def) => (
                  <Col xs={24} md={12} key={`${key}.${def.key}`}>
                    <Text style={{ display: 'block', marginBottom: 6 }}>{def.label || def.key}</Text>
                    <FilterControl
                      def={def}
                      value={audienceFilters[def.key]}
                      optionsMap={filterOptions}
                      onChange={(v) => setFilterValue(key, def.key, v)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </div>
        ),
      };
    });
  }, [selectedAudiences, audienceMeta, filters, filterOptions]);

  const visibleRows = useMemo(() => {
    if (historyChannel === 'all') return rows;
    return rows.filter((r) => r.channel === historyChannel);
  }, [rows, historyChannel]);

  const columns = useMemo(() => [
    {
      title: 'Campaign',
      key: 'campaign',
      render: (_, row) => {
        const meta = CAMPAIGN_TYPE_META[row.type] || { label: row.type, tagColor: 'default' };
        return (
          <Space direction="vertical" size={2} style={{ maxWidth: 360 }}>
            <Space size={6} wrap>
              <Tag
                color={row.channel === 'email' ? 'cyan' : 'blue'}
                icon={row.channel === 'email' ? <MailOutlined /> : <MobileOutlined />}
                style={{ margin: 0 }}
              >
                {row.channel === 'email' ? 'Email' : 'Push'}
              </Tag>
              <Tag color={meta.tagColor} style={{ margin: 0 }}>{meta.label || row.type}</Tag>
            </Space>
            <Text strong ellipsis style={{ maxWidth: 340 }}>{row.title || '—'}</Text>
          </Space>
        );
      },
    },
    { title: 'Target', dataIndex: 'detail', ellipsis: true, width: 180 },
    { title: 'Reach', dataIndex: 'counts', width: 120 },
    {
      title: 'When',
      dataIndex: 'when',
      width: 170,
      render: (v) => fmtDateTime(v),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (status, row) => (
        <Tooltip title={row.error_message || undefined}>
          <Tag color={CAMPAIGN_STATUS_COLORS[status] || 'default'}>{status}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_, row) => (
        <Space size="small" wrap>
          {['sent', 'failed'].includes(row.status) ? (
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openAudit(row)}>
              View
            </Button>
          ) : null}
          {['sent', 'failed'].includes(row.status) && Number(row.raw?.failed_count) > 0 ? (
            <Popconfirm
              title="Retry this campaign's failed items?"
              onConfirm={() => handleRetryFailed(row)}
              okText="Retry"
            >
              <Button type="link" size="small" icon={<RedoOutlined />} loading={retryingKey === row.key}>
                Retry failed
              </Button>
            </Popconfirm>
          ) : null}
          {['draft', 'scheduled', 'sending'].includes(row.status) ? (
            <Popconfirm
              title="Cancel this campaign?"
              onConfirm={() => handleCancel(row)}
              okText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                danger
                size="small"
                icon={<StopOutlined />}
                loading={cancellingKey === row.key}
              >
                Cancel
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ], [cancellingKey, retryingKey, handleCancel, handleRetryFailed, openAudit]);

  const pushTitleLen = title.trim().length;
  const pushBodyLen = stripHtml(body).length;
  const showActionBar = activeTab === 'compose';

  const composePanel = (
    <div className="campaigns-compose">
      <div className="campaigns-form">
        <section className="campaigns-section">
          <span className="campaigns-section__label">Channels</span>
          <div className="campaigns-channel-grid">
            <ChannelCard
              channel="push"
              active={usePush}
              onToggle={() => setChannels((c) => ({ ...c, push: !c.push }))}
              icon={<MobileOutlined />}
              title="Push"
              hint="Client-app devices. Target by organisation, plan and access type below, and pick the section the app opens."
            />
            <ChannelCard
              channel="email"
              active={useEmail}
              onToggle={() => setChannels((c) => ({ ...c, email: !c.email }))}
              icon={<MailOutlined />}
              title="Email"
              hint="Segmented by audience, tier, age, org, and more."
            />
          </div>
          {marketingPushConflict && (
            <Alert
              className="campaigns-warn"
              type="warning"
              showIcon
              message="Marketing cannot go out as push. Turn off Push or change the type."
            />
          )}
          {!usePush && !useEmail && (
            <Alert
              className="campaigns-warn"
              type="error"
              showIcon
              message="Select at least one channel."
            />
          )}
        </section>

        <section className="campaigns-section">
          <span className="campaigns-section__label">Type</span>
          <Segmented
            className="campaigns-type-segmented"
            block
            options={TYPE_SEGMENTED}
            value={type}
            onChange={setType}
          />
          {type === 'emergency' && (
            <Alert
              className="campaigns-warn"
              type="error"
              showIcon
              message="Emergency campaigns are high priority and reach selected channels immediately."
            />
          )}
        </section>

        {usePush && (
          <section className="campaigns-section">
            <span className="campaigns-section__label">Push audience & landing page</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Organisation</Text>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="All organisations"
                  style={{ width: '100%' }}
                  value={pushTargets.org_ids}
                  onChange={(v) => setPushTargets((t) => ({ ...t, org_ids: v }))}
                  options={(pushOptions.organizations || []).map((o) => ({ value: o.org_id, label: o.org_name }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Plan</Text>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="All plans"
                  style={{ width: '100%' }}
                  value={pushTargets.plan_ids}
                  onChange={(v) => setPushTargets((t) => ({ ...t, plan_ids: v }))}
                  options={(pushOptions.plans || []).map((p) => ({ value: p.plan_id, label: p.plan_name }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Access type</Text>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All access types"
                  style={{ width: '100%' }}
                  value={pushTargets.access_types}
                  onChange={(v) => setPushTargets((t) => ({ ...t, access_types: v }))}
                  options={(pushOptions.access_types || []).map((a) => ({ value: a.value, label: a.label }))}
                />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Opens</Text>
                <Select
                  allowClear
                  placeholder="Just open the app"
                  style={{ width: '100%' }}
                  value={pushLanding}
                  onChange={(v) => setPushLanding(v || null)}
                  options={(pushOptions.landing_pages || []).map((l) => ({ value: l.href, label: l.label }))}
                />
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              Filters combine: only members matching every filter receive the push. Leave all empty to reach every client device.
              The landing page is a high-level app section; deep links are not offered in this version.
            </Text>
          </section>
        )}

        {useEmail && (
          <section className="campaigns-section">
            <span className="campaigns-section__label">Email audience</span>
            <div className="campaigns-audience-chips" style={{ marginBottom: 14 }}>
              <Checkbox.Group
                options={audienceOptions}
                value={selectedAudiences}
                onChange={(vals) => {
                  setSelectedAudiences(vals);
                  setPreview(null);
                }}
              />
            </div>

            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <FilterOutlined style={{ color: '#64748b' }} />
              <Text strong style={{ fontSize: 13 }}>Filters</Text>
              {activeFilterCount > 0 ? (
                <Tag color="cyan">{activeFilterCount} active</Tag>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>Optional</Text>
              )}
            </div>

            {selectedAudiences.length === 0 ? (
              <Alert type="warning" showIcon message="Select at least one audience." />
            ) : (
              <Collapse size="small" items={filterPanels} defaultActiveKey={selectedAudiences.slice(0, 1)} />
            )}

            <Space wrap style={{ marginTop: 12 }}>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={handlePreview}
                loading={previewLoading}
              >
                Estimate recipients
              </Button>
              {activeFilterCount > 0 && (
                <Button onClick={() => { setFilters({}); setPreview(null); }}>Clear filters</Button>
              )}
            </Space>

            {preview && (
              <div className="campaigns-recipient-panel">
                <div className="campaigns-recipient-panel__count">
                  Estimated recipients:{' '}
                  {Number(preview.estimated_recipients || 0).toLocaleString()}
                </div>
                <div className="campaigns-recipient-panel__sample">
                  {(preview.sample || []).slice(0, 8).map((s) => (
                    <div key={`${s.audience_type}-${s.source_id}-${s.email}`}>
                      <Text code>{s.email}</Text>
                      {' · '}
                      {s.audience_type}
                      {' · '}
                      {s.name || '—'}
                    </div>
                  ))}
                  {(preview.sample || []).length === 0 && (
                    <Text type="secondary">No sample addresses returned.</Text>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        <section className="campaigns-section">
          <span className="campaigns-section__label">Message</span>

          <div style={{ marginBottom: 14 }}>
            <div className="campaigns-field-label">
              <span>{useEmail ? 'Title / Subject' : 'Title'}</span>
              {usePush ? (
                <span className={`meta${pushTitleLen > 60 ? ' ant-typography-danger' : ''}`}>
                  push {pushTitleLen}/60
                </span>
              ) : null}
            </div>
            <Input
              placeholder="Campaign title / email subject"
              maxLength={usePush && !useEmail ? 60 : 255}
              showCount={!usePush}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              status={usePush && pushTitleLen > 60 ? 'error' : undefined}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <div className="campaigns-field-label">
              <span>Body</span>
              <span className="meta">
                {usePush ? `push plain ${pushBodyLen}/250` : null}
                {usePush && useEmail ? ' · ' : null}
                {useEmail ? 'push uses this formatted text, stripped to plain' : null}
              </span>
            </div>
            {useEmail ? (
              <EmailWysiwygEditor
                value={body}
                onChange={setBody}
                placeholder="Write your email…"
              />
            ) : (
              <TextArea
                placeholder="Notification body"
                rows={7}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={250}
                showCount
                status={pushBodyLen > 250 ? 'error' : undefined}
              />
            )}
          </div>

          <div>
            <div className="campaigns-field-label">
              <span>Schedule</span>
              <span className="meta">Optional — leave empty to send now</span>
            </div>
            <DatePicker
              showTime
              style={{ width: '100%', maxWidth: 320 }}
              placeholder="Send immediately"
              value={scheduledAt}
              disabledDate={(current) => current && current < dayjs().startOf('minute')}
              onChange={setScheduledAt}
            />
          </div>
        </section>
      </div>

      <aside className="campaigns-preview-rail">
        <div className="campaigns-preview-card">
          <div className="campaigns-preview-card__title">Live preview</div>
          {usePush ? (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                Push notification
              </Text>
              <PushPreview type={type} title={title} body={body} />
            </>
          ) : null}
          {usePush && useEmail ? <div style={{ height: 14 }} /> : null}
          {useEmail ? (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                Email
              </Text>
              <EmailPreview subject={title} body={body} type={type} />
            </>
          ) : null}
          {!usePush && !useEmail ? (
            <div className="campaigns-preview-empty">
              Enable Push or Email to see a preview.
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );

  const historyPanel = (
    <div className="campaigns-history-card">
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 12px 0', flexWrap: 'wrap' }}>
        <Segmented
          value={historyChannel}
          onChange={setHistoryChannel}
          options={[
            { value: 'all', label: 'All' },
            { value: 'push', label: 'Push', icon: <MobileOutlined /> },
            { value: 'email', label: 'Email', icon: <MailOutlined /> },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchHistory} loading={loading}>
          Refresh
        </Button>
      </div>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={visibleRows}
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (t) => `${t} campaigns` }}
        scroll={{ x: 900 }}
        style={{ padding: 8 }}
      />
    </div>
  );

  return (
    <div className="campaigns-page">
      <div className="campaigns-page__header">
        <div>
          <Title level={3} className="campaigns-page__title">
            <span className="campaigns-page__title-icon">
              <NotificationOutlined />
            </span>
            Communications
          </Title>
          <div className="campaigns-page__subtitle">
            Compose once — send as push, email, or both. Deep filters apply to email.
          </div>
        </div>
        <CampaignsHowToButton onClick={openGuide} />
      </div>

      <ModuleNav items={MODULE_NAVS.campaigns} />
      <Tabs
        className="campaigns-page__tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'compose', label: 'Compose', children: composePanel },
          { key: 'history', label: 'History', children: historyPanel },
        ]}
      />

      {showActionBar && (
        <div className="campaigns-action-bar">
          <div className="campaigns-action-bar__inner">
            <div className="campaigns-action-bar__summary">
              {usePush && <Tag color="blue" icon={<MobileOutlined />}>Push</Tag>}
              {useEmail && <Tag color="cyan" icon={<MailOutlined />}>Email</Tag>}
              <Tag color={CAMPAIGN_TYPE_META[type]?.tagColor}>
                {CAMPAIGN_TYPE_META[type]?.label}
              </Tag>
              {useEmail && preview?.estimated_recipients != null && (
                <span>
                  ~{Number(preview.estimated_recipients).toLocaleString()} email recipients
                </span>
              )}
              {scheduledAt && (
                <span>Scheduled {scheduledAt.format('MMM D, HH:mm')}</span>
              )}
              {saveAsDraft && <Tag>Draft</Tag>}
            </div>
            <div className="campaigns-action-bar__actions">
              {useEmail && (
                <Checkbox
                  checked={saveAsDraft}
                  onChange={(e) => setSaveAsDraft(e.target.checked)}
                >
                  Save as draft
                </Checkbox>
              )}
              <Button onClick={resetForm}>Reset</Button>
              <Button
                type="primary"
                danger={type === 'emergency'}
                icon={<SendOutlined />}
                onClick={handleSendClick}
                disabled={!usePush && !useEmail}
              >
                {saveAsDraft
                  ? 'Save draft'
                  : scheduledAt
                    ? 'Schedule campaign'
                    : 'Send campaign'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Confirm campaign"
        open={confirmOpen}
        onOk={handleConfirm}
        confirmLoading={sending}
        onCancel={() => setConfirmOpen(false)}
        okText={
          saveAsDraft
            ? 'Save draft'
            : scheduledAt
              ? 'Schedule'
              : type === 'emergency'
                ? 'Send emergency'
                : 'Send'
        }
        okButtonProps={{ danger: type === 'emergency' }}
        width={640}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            {usePush && !saveAsDraft && <Tag color="blue" icon={<MobileOutlined />}>Push</Tag>}
            {useEmail && <Tag color="cyan" icon={<MailOutlined />}>Email</Tag>}
            <Tag color={CAMPAIGN_TYPE_META[type]?.tagColor}>
              {CAMPAIGN_TYPE_META[type]?.label}
            </Tag>
            {saveAsDraft && <Tag>Draft (push skipped)</Tag>}
          </div>

          {useEmail && (
            <div>
              Audiences:{' '}
              {selectedAudiences.map((a) => (
                <Tag key={a}>{CAMPAIGN_AUDIENCE_LABELS[a] || a}</Tag>
              ))}
            </div>
          )}

          {usePush && !saveAsDraft && (
            <Alert type="info" showIcon message="Push will go to all active client app devices." />
          )}

          {useEmail && preview?.estimated_recipients != null && (
            <Text>
              Estimated email recipients:{' '}
              <Text strong>{Number(preview.estimated_recipients).toLocaleString()}</Text>
            </Text>
          )}

          {scheduledAt && (
            <Text>
              Scheduled for: <Text strong>{scheduledAt.format('YYYY-MM-DD HH:mm')}</Text>
            </Text>
          )}

          <div className="campaigns-confirm-previews">
            {usePush && !saveAsDraft && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Push</Text>
                <PushPreview type={type} title={title} body={body} />
              </div>
            )}
            {useEmail && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>
                <EmailPreview subject={title} body={body} type={type} />
              </div>
            )}
          </div>
        </Space>
      </Modal>

      <CampaignsHowToModal
        open={howToOpen}
        required={howToRequired}
        onAcknowledge={acknowledgeHowTo}
        onClose={closeHowTo}
      />

      <Drawer
        title={auditRow ? `${auditRow.channel === 'email' ? 'Email' : 'Push'} #${auditRow.id} — ${auditRow.title || ''}` : 'Campaign report'}
        open={!!auditRow}
        onClose={closeAudit}
        width={860}
      >
        {auditRow && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
              {auditRow.channel === 'email' ? (
                <Radio.Group value={auditFilter} onChange={(e) => changeAuditFilter(e.target.value)} optionType="button" buttonStyle="solid">
                  <Radio.Button value="all">
                    All {((auditCounts.pending || 0) + (auditCounts.sent || 0) + (auditCounts.failed || 0) + (auditCounts.skipped || 0)).toLocaleString()}
                  </Radio.Button>
                  <Radio.Button value="sent">Sent {(auditCounts.sent || 0).toLocaleString()}</Radio.Button>
                  <Radio.Button value="failed">Failed {(auditCounts.failed || 0).toLocaleString()}</Radio.Button>
                  <Radio.Button value="skipped">Skipped {(auditCounts.skipped || 0).toLocaleString()}</Radio.Button>
                  <Radio.Button value="pending">Pending {(auditCounts.pending || 0).toLocaleString()}</Radio.Button>
                </Radio.Group>
              ) : (
                <Radio.Group value={auditFilter} onChange={(e) => changeAuditFilter(e.target.value)} optionType="button" buttonStyle="solid">
                  <Radio.Button value="all">
                    All {((auditCounts.accepted || 0) + (auditCounts.ticket_errors || 0)).toLocaleString()}
                  </Radio.Button>
                  <Radio.Button value="delivered">Delivered {(auditCounts.delivered || 0).toLocaleString()}</Radio.Button>
                  <Radio.Button value="pending">Pending {(auditCounts.pending_receipts || 0).toLocaleString()}</Radio.Button>
                  <Radio.Button value="failed">
                    Failed {((auditCounts.ticket_errors || 0) + (auditCounts.receipt_errors || 0)).toLocaleString()}
                  </Radio.Button>
                </Radio.Group>
              )}
              {Number(auditRow.raw?.failed_count) > 0 && (
                <Popconfirm
                  title="Retry this campaign's failed items?"
                  onConfirm={() => handleRetryFailed(auditRow)}
                  okText="Retry"
                >
                  <Button icon={<RedoOutlined />} loading={retryingKey === auditRow.key}>
                    Retry failed
                  </Button>
                </Popconfirm>
              )}
            </Space>

            <Table
              rowKey="id"
              size="small"
              columns={auditColumns}
              dataSource={auditItems}
              loading={auditLoading}
              pagination={{
                current: auditPagination.page,
                pageSize: auditPagination.pageSize,
                total: auditPagination.total,
                showSizeChanger: true,
                showTotal: (t) => `${t} ${auditRow.channel === 'email' ? 'recipients' : 'devices'}`,
                onChange: changeAuditPage,
              }}
              scroll={{ x: 700 }}
            />
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default CampaignsPage;
